import React, { useMemo, useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import ActiveUserFilter from "../components/ActiveUserFilter";
import ActiveUserTable from "../components/ActiveUserTable";
import { useActiveUsers } from "../hooks/useActiveUsers";
import { getActiveUsersForExport } from "../api/activeUserService";
import {
  getIdentifiersText,
  getSubscriptionHistoryText,
} from "../utils/userExportUtils";
import SegmentationTable from "../components/SegmentationTable";
import { DEFAULT_QUERY, TABLE_KEYS } from "../store/activeUsersSlice";

function deriveDynamicDate(period) {
  if (!period) return "";
  const today = new Date();
  let start = new Date();
  switch (period) {
    case "today":
      start = new Date();
      break;
    case "week":
      start = new Date(today.setDate(today.getDate() - 7));
      break;
    case "month":
      start = new Date(today.setMonth(today.getMonth() - 1));
      break;
    case "quarter":
      start = new Date(today.setMonth(today.getMonth() - 3));
      break;
    case "year":
      start = new Date(today.setFullYear(today.getFullYear() - 1));
      break;
    case "all":
      return "2023-04-01T00:00:00.000Z";
    default:
      start = new Date();
  }
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
}

function parseQueryFromSearchParams(searchParams) {
  const paramsObj = Object.fromEntries(searchParams.entries());
  return {
    filters: {
      period: paramsObj.period || "",
      registerDateFrom: paramsObj.registerDateFrom || "",
      registerDateTo: paramsObj.registerDateTo || "",
      isSubscribed: paramsObj.isSubscribed === "true",
      subscriptionActivatedFrom: paramsObj.subscriptionActivatedFrom || "",
      subscriptionActivatedTo: paramsObj.subscriptionActivatedTo || "",
      subscriptionType: paramsObj.subscriptionType || "",
      activityLevel: paramsObj.activityLevel || "",
      search: paramsObj.search || "",
      segmentCodes: paramsObj.segmentCodes
        ? paramsObj.segmentCodes.split(",").filter(Boolean)
        : [],
    },
    page: Number(paramsObj.page) || 1,
    pageLimit: Number(paramsObj.pageLimit) || 10,
    dynamicDate: paramsObj.dynamicDate || "",
  };
}

const ActiveUserPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const tableKey =
    pathname === "/active-users"
      ? TABLE_KEYS.activeUsers
      : TABLE_KEYS.segmentation;

  // 🔹 из URL query берётся только при первом заходе после перезагрузки,
  // дальше источник истины — redux store
  const initialQuery = useMemo(
    () => parseQueryFromSearchParams(searchParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableKey],
  );

  const {
    query,
    queryKey,
    setQuery,
    data,
    total,
    loading,
    error,
    sortOrder,
    setSortOrder,
  } = useActiveUsers(tableKey, initialQuery);

  useEffect(() => {
    const params = new URLSearchParams();

    params.set("page", query.page);
    params.set("pageLimit", query.pageLimit);
    params.set("dynamicDate", query.dynamicDate);

    Object.entries(query.filters).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          params.set(key, value.join(","));
        }
      } else if (value !== "" && value !== false) {
        params.set(key, value);
      }
    });

    // replace — чтобы синхронизация URL со стором не плодила записи в истории
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, tableKey, setSearchParams]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((total || 0) / (query.pageLimit || 10)));
  }, [total, query.pageLimit]);

  const handleApplyFilters = (nextFilters) => {
    const dynamicDate = deriveDynamicDate(nextFilters.period);
    setQuery((prev) => ({
      ...prev,
      filters: nextFilters,
      dynamicDate,
      page: 1,
    }));
  };

  const handleClearFilters = () => {
    setQuery(DEFAULT_QUERY);
  };

  const handlePageChange = (newPage) => {
    setQuery((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleExport = async () => {
    try {
      const res = await getActiveUsersForExport(query);
      const dataArray = res?.data || [];
      if (!Array.isArray(dataArray) || dataArray.length === 0) {
        alert("Нет данных для экспорта");
        return;
      }

      const formatted = dataArray.map((user) => ({
        "Номер телефона": user.userName || "Нет данных",
        ОС: user.operatingSystem || "Нет данных",
        "Дата регистрации": user.dateOfRegistration
          ? new Date(user.dateOfRegistration).toLocaleString("ru-RU")
          : "Нет данных",
        "Дата последней активности": user.dateOfLastAction
          ? new Date(user.dateOfLastAction).toLocaleString("ru-RU")
          : "Нет данных",
        Подписка: user.subscription || "Нет данных",
        "Срок подписки":
          user.subscriptionDate?.start && user.subscriptionDate?.end
            ? `${new Date(user.subscriptionDate.start).toLocaleDateString()} - ${new Date(user.subscriptionDate.end).toLocaleDateString()}`
            : "Нет данных",
        "БИН/ИИН": getIdentifiersText(user),
        "История подписки": getSubscriptionHistoryText(
          user.subscriptionPeriodHistory,
        ),
        "Количество лайков": user.countOfLikes || 0,
        Дизлайк: user.countOfDislikes || 0,
        "Мои лайки": user.countOfFavourites || 0,
        "Результат ТС": user.countOfTSData || 0,
        Анализ: user.countOfAnalysis || 0,
        Подборки: user.countOfSelection || 0,
        "Статистика платных фильтров": user.countOfPaidFilters || 0,
        "Выгрузка эксель": user.countOfExports || 0,
        Поиск: user.countOfTextFilters || 0,
        Активность: user.activityLevelName || "Нет данных",
      }));

      const ws = XLSX.utils.json_to_sheet(formatted);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Users");
      XLSX.writeFile(wb, "users_data.xlsx");
    } catch (e) {
      console.error("Ошибка экспорта:", e);
      alert("Ошибка экспорта данных в Excel");
    }
  };

  return (
    <div>
      <ActiveUserFilter
        initialFilters={query.filters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onExport={handleExport}
      />

      {tableKey === TABLE_KEYS.activeUsers ? (
        <ActiveUserTable
          data={data}
          loading={loading}
          error={error}
          total={total}
          page={query.page}
          pageLimit={query.pageLimit}
          onPageChange={handlePageChange}
          actionDateFrom={query.dynamicDate}
          sortOrder={sortOrder}
          onSortOrderChange={setSortOrder}
        />
      ) : (
        <SegmentationTable
          data={data}
          loading={loading}
          error={error}
          total={total}
          page={query.page}
          pageLimit={query.pageLimit}
          onPageChange={handlePageChange}
          actionDateFrom={query.dynamicDate}
        />
      )}
    </div>
  );
};

export default ActiveUserPage;
